<template>
  <div :data-testid="dataTestid" class="h-64 w-full">
    <VChart :option="option" :theme="OMR_ECHARTS_THEME" autoresize class="h-full w-full" :data-testid="`${dataTestid}-chart`" />
  </div>
</template>

<script lang="ts">
export interface InsightsBarRow {
  label: string
  value: number
  color?: string
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeCompact, makeRatioPercent } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    rows: InsightsBarRow[]
    locale: string
    max?: number
    trackColor?: string
    dataTestid?: string
  }>(),
  { max: undefined, trackColor: undefined, dataTestid: 'insights-bar-rows' },
)

const option = computed<OmrEChartsOption>(() => {
  const isShare = props.rows.length > 0 && props.rows.every((row) => row.value >= 0 && row.value <= 1)
  const format = isShare ? makeRatioPercent(props.locale, 1) : makeCompact(props.locale)

  return {
    tooltip: { trigger: 'axis', valueFormatter: (value) => format(Number(value)) },
    grid: { top: 8, left: 8, right: 56, bottom: 8, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: props.max,
      splitLine: { show: false },
      axisLabel: { show: false },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: props.rows.map((row) => row.label),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontWeight: 'bold' },
    },
    series: [
      {
        type: 'bar',
        data: props.rows.map((row) => ({ value: row.value, itemStyle: { color: row.color } })),
        barWidth: 10,
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        showBackground: true,
        backgroundStyle: { color: props.trackColor, borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          fontWeight: 'bold',
          formatter: (params) => format(Number(params.value)),
        },
      },
    ],
  }
})
</script>
